import { api } from './api';
import { getAsyncTextStream } from './stream';
import type { DocumentOptions, Project, TextCompletionProps } from './types';
import { logger } from './logger';

type CreateClientOptions = {
  endpoint?: string;
  apiKey?: string;
  getToken?: () => Promise<string>;
};

type PromptProps = {
  projectId: number;
  uid: string;
  name: string;
  prompt: string;
};

type Prompt = PromptProps & {
  id: number;
};

type NearestNeighbor = {
  id: number;
  name: string;
  type: string;
  metadata: { question: string; answer: string };
};
type AIClient = {
  setToken: (token: string) => void;
  // Text
  generateText: (props: TextCompletionProps) => Promise<string>;
  streamText: (
    props: TextCompletionProps,
    asStream?: boolean,
  ) => Promise<AsyncIterable<string> | ReadableStream<any>>;
  // Projects
  query: (props: {
    projectUid: string;
    query: string;
    topK?: number;
  }) => Promise<NearestNeighbor[]>;
  getProjects: () => Promise<Project[]>;
  getProject: (props: { projectId: number }) => Promise<Project>;
  createProject: (props: {
    projectName: string;
    uid: string;
  }) => Promise<number>;
  deleteProject: (props: { projectId: number }) => Promise<boolean>;
  // Documents
  getDocument: (props: { documentId: number }) => Promise<Document>;
  addDocument: (props: DocumentOptions) => Promise<number>;
  uploadFile: (props: { projectId: number; file: File }) => Promise<number>;
  deleteDocument: (props: { documentId: number }) => Promise<boolean>;
  generateEmbeddings: (text: string) => Promise<number[]>;
  // Prompts
  getPrompts: (props: { projectId: number }) => Promise<Prompt[]>;
  getPrompt: (
    promptUid: string,
    variables?: Record<string, string>,
  ) => Promise<string>;
  addPrompt: (props: PromptProps) => Promise<number>;
  updatePrompt: (props: Prompt) => Promise<boolean>;
  deletePrompt: (props: { promptId: number }) => Promise<boolean>;
};

function isPromise(value: any) {
  return Boolean(value && typeof value.then === 'function');
}

function isServer() {
  return typeof window === 'undefined';
}

const getEndpoint = () => {
  return (
    process.env.AI_ENDPOINT ||
    process.env.VITE_AI_ENDPOINT ||
    process.env.NEXT_PUBLIC_AI_ENDPOINT
  );
};

export function createAIClient({
  endpoint,
  apiKey,
  getToken,
}: CreateClientOptions): AIClient {
  endpoint = endpoint ?? getEndpoint();
  if (!endpoint) {
    throw new Error(
      'AI endpoint is not set. Either pass it as an option or set AI_ENDPOINT or NEXT_PUBLIC_AI_ENDPOINT',
    );
  }

  if (!isServer() && apiKey) {
    logger.error(
      'Providing an API key on the client side is not secure. The API key will be exposed to anyone who views your source code. Consider using JWT authentication instead.',
    );
  }

  let token: string | undefined = undefined;

  const getHeaders = async () => {
    let t = token;
    if (getToken) {
      t = await getToken();
    }
    return {
      ...(t && { Authorization: `Bearer ${t}` }),
      ...(apiKey && { 'x-api-key': apiKey }),
    };
  };

  return {
    // The token is forwarded on every request by getHeaders() below, which
    // builds the `Authorization: Bearer` header passed to api.headers().
    setToken: (t: string) => {
      token = t;
    },
    generateText: async (props: TextCompletionProps) => {
      if (isPromise(props.system)) {
        props.system = await props.system;
      }
      const res = await api
        .headers(await getHeaders())
        .post(`${endpoint}/text/generate-text`, {
          json: props,
        })
        .json<{ text: string }>();
      return res.text;
    },

    streamText: async (props: TextCompletionProps, asStream = false) => {
      if (isPromise(props?.system)) {
        props.system = await props.system;
      }
      const res = await api
        .headers(await getHeaders())
        .post(`${endpoint}/text/stream-text`, {
          json: props,
        });

      if (asStream) {
        return res.body as ReadableStream<any>;
      }
      return getAsyncTextStream(res.body as ReadableStream<any>);
    },

    query: async (props: {
      projectUid: string;
      query: string;
      topK?: number;
    }) => {
      const queryParams = new URLSearchParams({
        projectUId: props.projectUid,
        query: props.query,
        ...(props.topK && { topK: props.topK.toString() }),
      });
      const res = await api
        .headers(await getHeaders())
        .get(`${endpoint}/projects/${props.projectUid}/query?${queryParams}`)
        .json<{ nearestNeighbors: NearestNeighbor[] }>();
      return res.nearestNeighbors;
    },

    getProjects: async () => {
      const res = await api
        .headers(await getHeaders())
        .get(`${endpoint}/projects`)
        .json<{ projects: Project[] }>();
      return res.projects;
    },

    getProject: async (props: { projectId: number }) => {
      const res = await api
        .headers(await getHeaders())
        .get(`${endpoint}/projects/${props.projectId}`)
        .json<{ project: Project }>();
      return res.project;
    },

    createProject: async (props: { projectName: string; uid: string }) => {
      const res = await api
        .headers(await getHeaders())
        .post(`${endpoint}/projects`, { json: props })
        .json<{ projectId: number }>();
      return res.projectId;
    },

    deleteProject: async (props: { projectId: number }) => {
      const res = await api
        .headers(await getHeaders())
        .delete(`${endpoint}/projects/${props.projectId}`)
        .json<{ success: boolean }>();
      return res.success;
    },

    addDocument: async (document: DocumentOptions) => {
      const res = await api
        .headers(await getHeaders())
        .post(`${endpoint}/projects/${document.projectId}/documents`, {
          json: document,
        })
        .json<{ documentId: number }>();
      return res.documentId;
    },

    getDocument: async (props: { documentId: number }) => {
      const res = await api
        .headers(await getHeaders())
        .get(`${endpoint}/documents/${props.documentId}`)
        .json<{ document: Document }>();
      return res.document;
    },

    uploadFile: async (props: { projectId: number; file: File }) => {
      const formData = new FormData();
      formData.append('document', props.file);
      const res = await api
        .headers(await getHeaders())
        .post(`${endpoint}/projects/${props.projectId}/documents/upload`, {
          formData,
        })
        .json<{ documentId: number }>();
      return res.documentId;
    },

    deleteDocument: async ({ documentId }: { documentId: number }) => {
      const res = await api
        .headers(await getHeaders())
        .delete(`${endpoint}/documents/${documentId}`)
        .json<{ success: boolean }>();
      return res.success;
    },

    generateEmbeddings: async (text: string) => {
      const res = await api
        .headers(await getHeaders())
        .get(`${endpoint}/embedding/generate?text=${text}`)
        .json<{ vector: number[] }>();
      return res.vector;
    },

    getPrompts: async ({ projectId }: { projectId: number }) => {
      const res = await api
        .headers(await getHeaders())
        .get(`${endpoint}/projects/${projectId}/prompts`)
        .json<{ prompts: Prompt[] }>();
      return res.prompts;
    },

    getPrompt: async (
      promptUid: string,
      variables?: Record<string, string>,
    ) => {
      if (!promptUid) {
        return '';
      }
      const res = await api
        .headers(await getHeaders())
        .get(`${endpoint}/prompts/${promptUid}`)
        .json<{ prompt: Prompt }>();

      if (!res.prompt) {
        logger.error(`Prompt not found`, { promptUid });
        return '';
      }

      let interpolatedPrompt = res.prompt?.prompt;
      if (variables) {
        for (const [key, value] of Object.entries(variables)) {
          interpolatedPrompt = interpolatedPrompt.replace(
            new RegExp(`{{${key}}}`, 'g'),
            value,
          );
        }
      }

      return interpolatedPrompt;
    },

    addPrompt: async ({ projectId, ...props }: PromptProps) => {
      const res = await api
        .headers(await getHeaders())
        .post(`${endpoint}/projects/${projectId}/prompts`, {
          json: props,
        })
        .json<{ promptId: number }>();
      return res.promptId;
    },

    updatePrompt: async (props: Prompt) => {
      const res = await api
        .headers(await getHeaders())
        .put(`${endpoint}/prompts/${props.id}`, {
          json: props,
        })
        .json<{ success: boolean }>();
      return res.success;
    },

    deletePrompt: async ({ promptId }: { promptId: number }) => {
      const res = await api
        .headers(await getHeaders())
        .delete(`${endpoint}/prompts/${promptId}`)
        .json<{ success: boolean }>();
      return res.success;
    },
  };
}
